import { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, MapPin, Building2 } from 'lucide-react';
import { useEmployee } from '../contexts/EmployeeContext';
import TeamIcon from '../components/TeamIcon';
import type { Employee, Team } from '../types/employee';

function findTeam(employees: Employee[], teamId: string): Team | undefined {
  for (const e of employees) {
    const t = (e.teams ?? []).find(t => t.id === teamId);
    if (t) return t;
  }
  return undefined;
}

const TeamPage = () => {
  const { id } = useParams<{ id: string }>();
  const { employees, isLoading } = useEmployee();
  const navigate = useNavigate();

  const team = id ? findTeam(employees, id) : undefined;
  const members = id
    ? employees.filter(e => (e.teams ?? []).some(t => t.id === id))
    : [];

  useEffect(() => {
    if (team) document.title = `${team.name} - Global Team Directory`;
  }, [team]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!team) {
    return (
      <div className="text-center py-24">
        <h2 className="text-xl font-semibold text-bolt-text mb-3">Team Not Found</h2>
        <p className="text-sm text-bolt-textMuted mb-8">
          This team doesn't exist or no members have joined it yet.
        </p>
        <Link to="/" className="btn btn-primary">Back to Directory</Link>
      </div>
    );
  }

  // Group members by country for the summary strip
  const countryCount = members.reduce<Record<string, number>>((acc, e) => {
    acc[e.location.country] = (acc[e.location.country] ?? 0) + 1;
    return acc;
  }, {});
  const topCountries = Object.entries(countryCount)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4);

  return (
    <div className="animate-fade-in space-y-6">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-sm text-bolt-textMuted hover:text-bolt-text transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        <span>Back</span>
      </button>

      {/* Hero card */}
      <div className="card relative overflow-hidden">
        {/* Subtle tinted background strip */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/40 via-transparent to-transparent pointer-events-none" />
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              'repeating-linear-gradient(0deg, transparent, transparent 31px, rgba(255,255,255,0.6) 31px, rgba(255,255,255,0.6) 32px), repeating-linear-gradient(90deg, transparent, transparent 31px, rgba(255,255,255,0.6) 31px, rgba(255,255,255,0.6) 32px)',
          }}
        />

        <div className="relative px-6 py-6 flex flex-col gap-4">
          {/* Icon + title row */}
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-bolt-surfaceHover border border-bolt-border flex items-center justify-center shadow-md flex-shrink-0">
              <TeamIcon team={team} size={28} className="text-bolt-text" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-bolt-text leading-tight">{team.name}</h1>
              <p className="text-sm text-bolt-textSubtle mt-0.5">
                {members.length} {members.length === 1 ? 'member' : 'members'}
              </p>
            </div>
          </div>

          {/* Stats row */}
          {topCountries.length > 0 && (
            <div className="flex flex-wrap items-center gap-4 pt-1 border-t border-bolt-border/60">
              <div className="flex items-center gap-1.5 text-xs text-bolt-textSubtle">
                <Users className="h-3.5 w-3.5" />
                <span className="font-medium text-bolt-textMuted">{members.length}</span>
                <span>{members.length === 1 ? 'member' : 'members'}</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-bolt-textSubtle">
                <MapPin className="h-3.5 w-3.5" />
                <span className="font-medium text-bolt-textMuted">{Object.keys(countryCount).length}</span>
                <span>{Object.keys(countryCount).length === 1 ? 'country' : 'countries'}</span>
              </div>
              <div className="flex items-center gap-2 ml-auto flex-wrap justify-end">
                {topCountries.map(([country, count]) => (
                  <span key={country} className="inline-flex items-center gap-1 text-xs text-bolt-textSubtle">
                    <Building2 className="h-3 w-3" />
                    {country}
                    <span className="text-bolt-textMuted font-medium">({count})</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Members grid */}
      {members.length === 0 ? (
        <div className="text-center py-16 text-bolt-textSubtle text-sm">
          No members have joined this team yet.
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-4 w-4 text-bolt-textSubtle" />
            <h2 className="text-xs font-semibold uppercase tracking-widest text-bolt-textSubtle">
              Members
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {members.map(member => (
              <MemberCard key={member.id} member={member} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MemberCard = ({ member }: { member: Employee }) => (
  <Link
    to={`/employee/${member.id}`}
    className="group flex flex-col items-center gap-2.5 p-4 rounded-xl bg-bolt-surface border border-bolt-border hover:border-primary-500/40 hover:bg-bolt-surfaceHover transition-all text-center"
  >
    <div className="relative">
      <img
        src={member.avatar}
        alt={member.name}
        className="w-14 h-14 rounded-full object-cover ring-2 ring-bolt-border group-hover:ring-primary-500/40 transition-all"
        onMouseEnter={e => { if (member.avatarHover) (e.currentTarget as HTMLImageElement).src = member.avatarHover; }}
        onMouseLeave={e => { (e.currentTarget as HTMLImageElement).src = member.avatar; }}
      />
    </div>
    <div className="min-w-0 w-full space-y-0.5">
      <p className="text-xs font-semibold text-bolt-text truncate">{member.name}</p>
      <p className="text-[11px] text-bolt-textSubtle truncate">{member.role}</p>
      <p className="text-[11px] text-bolt-textSubtle truncate flex items-center justify-center gap-1">
        <MapPin className="h-2.5 w-2.5 flex-shrink-0" />
        {[member.location.city, member.location.country].filter(Boolean).join(', ')}
      </p>
    </div>
  </Link>
);

export default TeamPage;
